import { Injectable } from '@angular/core';

import { Observable, of, forkJoin } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AccountBalanceInfo } from './account-balance-info';

import { PruHttpService } from '../../../../services/pru-http.service';

@Injectable({
  providedIn: 'root'
})
export class AccountsBalanceService {
  private defaultApiUrl = '/api/secure/direct-account/v2/acctservice/accounts/{referenceId}/account-values';

  private balanceTypeMapping = {
    sia: 'fullnetworth',
    unknown: 'unknown'
  };

  constructor(
    private pruHttpService: PruHttpService
  ) {}

  getAccountsBalance(productType: string, referenceIds: string[]): Observable<AccountBalanceInfo[]> {
    if (!productType || !referenceIds || referenceIds.length === 0) {
      return of([]);
    }

    const accountBalanceObservableList = [];

    for (const referenceId of referenceIds) {
      accountBalanceObservableList.push(
        this.getAccountBalanceByReference(referenceId)
      );
    }

    return forkJoin(accountBalanceObservableList)
    .pipe(
      switchMap(accountBalanceInfos => {
          return of(this.prepareAccountBalanceInfo(accountBalanceInfos, productType));
        }
      )
    );
  }

  private getAccountBalanceByReference(referenceId: string): Observable<any> {
    return this.pruHttpService.get(this.getApiUrl(referenceId), 'AccountsBalanceService').pipe(
      catchError(() => of(null))
    );
  }

  private getApiUrl(referenceId: string): string {
    return this.defaultApiUrl.replace('{referenceId}', referenceId);
  }

  private prepareAccountBalanceInfo(balanceInfos: any[], productType: string): AccountBalanceInfo[] {
    if (!balanceInfos || balanceInfos.length === 0) {
      return balanceInfos;
    }

    balanceInfos = balanceInfos.map((balanceInfo: any) => {
      if (!balanceInfo || !balanceInfo.accountValues) {
        return null;
      }

      const balanceAndDate = this.getBalanceAndDate(productType, balanceInfo.accountValues);

      if (!balanceAndDate) {
        return null;
      }

      return {
        productType,
        referenceId: balanceInfo.referenceId,
        accountValue: balanceAndDate ? balanceAndDate.accountValue : 0,
        asOfDate: balanceAndDate ? balanceAndDate.asOfDate : ''
      };
    });

    return balanceInfos.filter(item => !!item);
  }

  private getBalanceAndDate(productType: string, accountValues: any[]): any {
    const balanceType = this.balanceTypeMapping[productType.toLowerCase()] || this.balanceTypeMapping.unknown;

    const foundAccountValues = accountValues.filter((accountValue: any) => {
      return accountValue && accountValue.type && accountValue.type.toLowerCase() === balanceType;
    });

    if (foundAccountValues && foundAccountValues.length > 0) {
      return {
        accountValue: foundAccountValues[0].amount,
        asOfDate: this.formatAsOfDate(foundAccountValues[0].asOfDate)
      };
    } else {
      return null;
    }
  }

  // will come back to as format not stable yet. - KM
  private formatAsOfDate(rawDate: string): string {
    try {
      if (rawDate.indexOf('-') > 0) {
        let splits;

        if (rawDate.indexOf('T') > 0) {
          splits = rawDate.split('T')[0].split('-');
        } else {
          splits = rawDate.split(' ')[0].split('-');
        }

        return splits[1] + '/' + splits[2] + '/' + splits[0];
      }

      return rawDate;
    } catch (e) {
      return rawDate;
    }
  }
}
